import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import {
  isGoogleMapsConfigured,
  fetchAddressSuggestions,
  resolveAddressSuggestion,
  type AddressSuggestion,
  type ResolvedPlace,
} from './googleMaps';

interface AddressAutocompleteInputProps {
  value: string;
  onChange: (address: string) => void;
  /** A kiválasztott hely — koordinátákkal ÉS a Google Place ID-val. */
  onPlaceSelected: (place: ResolvedPlace) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  /**
   * Szűkítés hely-típusra (pl. `CITY_PLACE_TYPES` a település szintű
   * bevitelhez). Üresen hagyva minden találatot elfogad.
   */
  includedPrimaryTypes?: string[];
  /** Hány karaktertől kérjen javaslatot (alapértelmezés 3). */
  minLength?: number;
  /** Súgó szöveg a mező alatt; `null` esetén nincs. */
  hint?: string | null;
}

/**
 * Cím bevitel a bekerülési űrlaphoz. Ha van konfigurált Google Maps API kulcs,
 * élő címjavaslatokat kér az új Places API-tól (AutocompleteSuggestion) gépelés
 * közben, saját legördülőben — a kiválasztás kitölti a koordinátákat is. Kulcs
 * hiányában (vagy elutasított kulcs/kérés esetén) sima szöveges input marad.
 */
export const AddressAutocompleteInput: React.FC<AddressAutocompleteInputProps> = ({
  value, onChange, onPlaceSelected,
  placeholder = 'Pl. Budapest, XI. kerület, Bartók Béla út 12.',
  className = 'w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500',
  id,
  includedPrimaryTypes,
  minLength = 3,
  hint = 'Kezdj el gépelni — válaszd ki a listából a címet, a koordináták automatikusan kitöltődnek.',
}) => {
  const configured = isGoogleMapsConfigured();
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef<number | null>(null);
  const blurTimer = useRef<number | null>(null);

  useEffect(() => () => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (blurTimer.current) window.clearTimeout(blurTimer.current);
  }, []);

  const runSearch = (raw: string) => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    const q = raw.trim();
    if (!configured || q.length < minLength) {
      setSuggestions([]); setOpen(false); return;
    }
    debounceRef.current = window.setTimeout(async () => {
      setBusy(true);
      const res = await fetchAddressSuggestions(q, { region: 'hu', includedPrimaryTypes, minLength });
      setBusy(false);
      setSuggestions(res);
      setActiveIndex(-1);
      setOpen(res.length > 0);
    }, 250);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    runSearch(e.target.value);
  };

  const handleSelect = async (s: AddressSuggestion) => {
    setOpen(false);
    setSuggestions([]);
    setActiveIndex(-1);
    onChange(s.text);
    setBusy(true);
    const place = await resolveAddressSuggestion(s);
    setBusy(false);
    if (place) {
      onChange(place.address);
      onPlaceSelected(place);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <input
        id={id}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        onBlur={() => { blurTimer.current = window.setTimeout(() => setOpen(false), 150); }}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
      />
      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
      </div>

      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-auto">
          {suggestions.map((s, i) => (
            <li key={s.id}>
              <button
                type="button"
                // onMouseDown (not onClick) so the pick fires before the input's blur closes the list.
                onMouseDown={e => { e.preventDefault(); handleSelect(s); }}
                className={`w-full text-left px-3 py-2 text-sm flex items-start gap-2 ${
                  i === activeIndex ? 'bg-teal-50 text-teal-800' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
                <span>{s.text}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {configured && hint && (
        <p className="text-xs text-gray-400 mt-1.5">{hint}</p>
      )}
    </div>
  );
};

export default AddressAutocompleteInput;
